import { motion } from "framer-motion";
import { MessageSquare, Lightbulb, Code, Rocket } from "lucide-react";

const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      icon: MessageSquare,
      title: "Descubrimiento",
      description:
        "Analizamos tu negocio, competencia y objetivos para crear una estrategia personalizada.",
      color: "#2979FF",
    },
    {
      number: "02",
      icon: Lightbulb,
      title: "Estrategia & Diseño",
      description:
        "Diseñamos la arquitectura y UX/UI que maximizará la conversión y experiencia de usuario.",
      color: "#00E5FF",
    },
    {
      number: "03",
      icon: Code,
      title: "Desarrollo Ágil",
      description:
        "Construimos con las últimas tecnologías, iterando y refinando en sprints semanales.",
      color: "#8B5CF6",
    },
    {
      number: "04",
      icon: Rocket,
      title: "Lanzamiento & Escala",
      description:
        "Desplegamos, optimizamos y escalamos. Tu éxito es nuestro compromiso continuo.",
      color: "#10B981",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section
      id="proceso"
      className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden"
      data-testid="process-section"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#0A0A0A] to-[#030303]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF] mb-4 block">
            Cómo Trabajamos
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4">
            Un proceso diseñado para el{" "}
            <span className="gradient-text">éxito</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Metodología probada que garantiza resultados excepcionales en cada proyecto
          </p>
        </motion.div>

        {/* Process Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="relative"
                data-testid={`process-step-${index + 1}`}
              >
                {/* Connector line (hidden on last item and mobile) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-[60%] w-full h-[2px]">
                    <div
                      className="h-full w-full"
                      style={{
                        background: `linear-gradient(to right, ${step.color}40, transparent)`,
                      }}
                    />
                  </div>
                )}

                <div className="relative group">
                  {/* Number badge */}
                  <div
                    className="absolute -top-3 -left-3 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      background: step.color,
                      boxShadow: `0 0 20px ${step.color}40`,
                    }}
                  >
                    {step.number}
                  </div>

                  {/* Card */}
                  <div className="bg-[#0A0A0A] border border-white/5 rounded-3xl p-8 pt-10 group-hover:border-white/10 transition-all duration-500">
                    {/* Icon */}
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                      style={{
                        background: `${step.color}15`,
                      }}
                    >
                      <Icon className="w-8 h-8" style={{ color: step.color }} />
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-zinc-400 leading-relaxed text-sm">
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-16"
        >
          <p className="text-zinc-400 mb-6">
            ¿Listo para comenzar tu proyecto?
          </p>
          <a
            href="#contacto"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2979FF] to-[#00E5FF] text-white px-8 py-4 rounded-full font-medium hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] hover:-translate-y-1 transition-all duration-300"
            data-testid="process-cta"
          >
            Iniciar Conversación
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;
